import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, map, Observable, Subject, takeUntil } from 'rxjs';

import { TriggerAction } from '@shared/actions';
import { StateManagerProjectService } from '@state-manager';
import { CornerstoneService } from '@viewer/cornerstone.service';
import { CornerstoneTool, ViewWindowIndex } from '@viewer/data';
import { BaseToolComponent } from '../base-tool/base-tool.component';
import { KeyInteractionService } from 'src/app/interaction';
import {
  CompositeKeys,
  HotKey,
  HotKeySettings,
  UndoRedoComponentKeyActionName,
  UndoRedoComponentKeyActionNameList,
} from '@viewer/data';

@Component({
  selector: 'app-undo-redo',
  templateUrl: 'undo-redo.component.html',
  styleUrls: ['undo-redo.component.scss'],
})
export class UndoRedoComponent
  extends BaseToolComponent
  implements OnInit, OnDestroy
{
  @Input() useHotKeys = true;

  public undoAction!: TriggerAction;
  public redoAction!: TriggerAction;

  private _unsubscribe$ = new Subject<void>();

  constructor(
    private _cornerstoneService: CornerstoneService,
    protected _stateManagerProjectService: StateManagerProjectService,
    private _keyInteractionService: KeyInteractionService
  ) {
    super(_stateManagerProjectService);
    this._initActions();
  }

  ngOnInit(): void {
    if (this.useHotKeys) {
      this._initializeHotKeys();
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();

    if (this.useHotKeys) {
      this._keyInteractionService.removeKeySettingsList([
        ...UndoRedoComponentKeyActionNameList,
      ]);
    }
  }

  private _initActions(): void {
    this.undoAction = new TriggerAction(
      'arrow-undo',
      '',
      this._onUndoBtnClick.bind(this)
    );

    this.undoAction.isEnable = this._isUndoEnable.bind(this);

    this.redoAction = new TriggerAction(
      'arrow-redo',
      '',
      this._onRedoBtnClick.bind(this)
    );

    this.redoAction.isEnable = this._isRedoEnable.bind(this);
  }

  public isUndoDisabled(): Observable<boolean> {
    return this._isUndoEnable().pipe(map((isUndoEnable) => !isUndoEnable));
  }

  public isRedoDisabled(): Observable<boolean> {
    return this._isRedoEnable().pipe(map((isRedoEnable) => !isRedoEnable));
  }

  private _isUndoEnable(): Observable<boolean> {
    return combineLatest([
      this.isDisabled$,
      this._stateManagerProjectService.isDisabledAllToolsForChangeMask$,
    ]).pipe(
      map(
        ([isDisabled, isDisabledAllToolsForChangeMask]) =>
          !isDisabled &&
          !this._isOtherModeEnable() &&
          !this._cornerstoneService.isUndoEmpty() &&
          !isDisabledAllToolsForChangeMask
      )
    );
  }

  private _isRedoEnable(): Observable<boolean> {
    return combineLatest([
      this.isDisabled$,
      this._stateManagerProjectService.isDisabledAllToolsForChangeMask$,
    ]).pipe(
      map(
        ([isDisabled, isDisabledAllToolsForChangeMask]) =>
          !isDisabled &&
          !this._isOtherModeEnable() &&
          !this._cornerstoneService.isRedoEmpty() &&
          !isDisabledAllToolsForChangeMask
      )
    );
  }

  private _onUndoBtnClick(): void {
    this._stateManagerProjectService.is3dUpdateNeeded = true;
    this._cornerstoneService.undo();
  }

  private _onRedoBtnClick(): void {
    this._stateManagerProjectService.is3dUpdateNeeded = true;
    this._cornerstoneService.redo();
  }

  private _isOtherModeEnable(): boolean {
    const indexes = [
      ViewWindowIndex.Coronal,
      ViewWindowIndex.Axial,
      ViewWindowIndex.Sagittal,
    ];

    return (
      [
        this._stateManagerProjectService.isProcessOfResetDiff,
        this._stateManagerProjectService.isProcessOfShowDiffHide,
      ].reduce((acc, flagObj) => {
        if (acc) {
          return acc;
        }

        return indexes.some((index) => flagObj[`viewer${index}`]);
      }, false) || this._stateManagerProjectService.isActiveShowChangesMode
    );
  }

  private _initializeHotKeys(): void {
    this._keyInteractionService
      .initializeHotKey(
        HotKey.Z,
        new HotKeySettings<UndoRedoComponentKeyActionName>({
          keyActionName: 'UndoTrigger',
          compositeKeys: [CompositeKeys.Ctrl],
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this.undoAction.trigger();
      });

    this._keyInteractionService
      .initializeHotKey(
        HotKey.Y,
        new HotKeySettings<UndoRedoComponentKeyActionName>({
          keyActionName: 'RedoTrigger',
          compositeKeys: [CompositeKeys.Ctrl],
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this.redoAction.trigger();
      });
  }
}
